<template>
  <div class="mt-1">
    <b-card>
      <b-overlay :show="loading" rounded="sm" no-fade>
        <b-row class="mb-1">
          <b-col cols="12" md="7" class="d-flex align-items-center justify-content-start mb-1 mb-md-0">
            <b-button variant="outline-primary" class="btn-icon" :to="{ path: '/settings' }">
              <i class="fa-solid fa-arrow-left"></i>
              <span class="ml-25">Back</span>
            </b-button>
            <h3 class="ml-1">Risks</h3>
          </b-col>
          <b-col cols="12" md="5">
            <div class="d-flex align-items-center justify-content-end">
              <div class="d-flex align-items-center mr-1">
                <label>Show</label>
                <v-select v-model="perPage" :options="perPageOptions" :clearable="false" class="per-page-selector d-inline-block mx-50" />
                <label>entries</label>
              </div>
              <b-form-input v-model="filter" class="d-inline-block mr-1" placeholder="Search..." />
              <b-button variant="primary" @click="$router.push({ name: 'settings-risk-objective-add' })">
                <span class="text-nowrap">Add Risk</span>
              </b-button>
            </div>
          </b-col>
        </b-row>

        <b-table
          sticky-header striped hover bordered
          :outlined="true"
          :fields="fields"
          :items="filteredItems"
          responsive="sm"
          :small="true"
          :filter="filter"
          @filtered="onFiltered"
          :filter-included-fields="['title']"
          show-empty
          empty-text="No matching records found"
          :current-page="currentPage"
          :per-page="perPage"
          @row-clicked="(row) => $router.push({ name: 'settings-risk-objective-edit', params: { id: row.id } })"
        >
          <template #cell(risk_category)="data">
            <span>{{ data.item.risk_category ? data.item.risk_category.title : '—' }}</span>
          </template>
          <template #cell(status)="data">
            <b-badge pill :variant="data.item.status === 'Active' ? 'success' : 'light-secondary'" class="text-capitalize">
              {{ data.item.status === 'Active' ? 'Active' : 'Archive' }}
            </b-badge>
          </template>
          <template #cell(actions)="data">
            <b-button variant="warning" class="mr-25 p-50" @click="$router.push({ name: 'settings-risk-objective-edit', params: { id: data.item.id } })">
              <i class="fa-solid fa-pen-to-square mr-25"></i> Edit
            </b-button>
            <b-button variant="primary" class="p-50" @click="toggleStatus(data.item)">
              {{ data.item.status === 'Active' ? 'Archive' : 'Activate' }}
            </b-button>
          </template>
        </b-table>

        <div class="mx-2 mb-1 mt-1">
          <b-row>
            <b-col cols="12" sm="6" class="d-flex align-items-center justify-content-center justify-content-sm-start">
              <span class="text-muted">Showing {{ dataMeta.from }} to {{ dataMeta.to }} of {{ dataMeta.of }} entries</span>
            </b-col>
            <b-col cols="12" sm="6" class="d-flex align-items-center justify-content-center justify-content-sm-end">
              <b-pagination v-model="currentPage" :total-rows="totalRows" :per-page="perPage" first-number last-number class="mb-0 mt-1 mt-sm-0" prev-class="prev-item" next-class="next-item">
                <template #prev-text><feather-icon icon="ChevronLeftIcon" size="18" /></template>
                <template #next-text><feather-icon icon="ChevronRightIcon" size="18" /></template>
              </b-pagination>
            </b-col>
          </b-row>
        </div>
      </b-overlay>
    </b-card>
  </div>
</template>

<script>
import { BOverlay, BCard, BRow, BCol, BFormInput, BButton, BTable, BBadge, BPagination } from 'bootstrap-vue';
import store from '@/store';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';
import { onUnmounted } from '@vue/composition-api';
import settingsStoreModule from '../settingsStoreModule';
import vSelect from 'vue-select';

export default {
  components: { BOverlay, BCard, BRow, BCol, BFormInput, BButton, BTable, BBadge, BPagination, vSelect, ToastificationContent },
  setup() {
    if (!store.hasModule('settings')) store.registerModule('settings', settingsStoreModule);
    onUnmounted(() => { if (store.hasModule('settings')) store.unregisterModule('settings'); });
  },
  data() {
    return {
      items: [], filter: null, loading: false,
      currentPage: 1, perPage: 50, totalRows: 0,
      perPageOptions: [10, 25, 50, 100],
      fields: [
        { key: 'title', label: 'Risk', sortable: true, thStyle: 'width:50%;color:white!important' },
        { key: 'risk_category', label: 'Risk Category', sortable: false, thStyle: 'width:20%;color:white!important' },
        { key: 'status', sortable: false, class: 'text-center', thStyle: 'width:10%;color:white!important' },
        { key: 'actions', sortable: false, class: 'text-center', thStyle: 'width:20%;color:white!important' },
      ],
    };
  },
  computed: {
    filteredItems() {
      if (!this.filter) return this.items;
      const s = this.filter.toLowerCase();
      return this.items.filter((i) => i.title.toLowerCase().includes(s));
    },
    dataMeta() {
      const from = this.totalRows !== 0 ? this.perPage * (this.currentPage - 1) + 1 : 0;
      const rawTo = this.perPage * (this.currentPage - 1) + Number(this.perPage);
      return { from, to: rawTo < this.totalRows ? rawTo : this.totalRows, of: this.totalRows };
    },
  },
  methods: {
    load() {
      this.loading = true;
      store.dispatch('settings/getAllRiskObjectives').then((res) => {
        this.items = res.data;
        this.totalRows = res.data.length;
        this.loading = false;
      }).catch(() => { this.loading = false; });
    },
    toggleStatus(item) {
      this.$bvModal.msgBoxConfirm(`Change status of "${item.title}"?`, {
        size: 'sm', title: 'Confirm', okVariant: 'primary', okTitle: 'Yes', cancelTitle: 'No',
        cancelVariant: 'outline-secondary', hideHeaderClose: true, centered: true,
      }).then((ok) => {
        if (!ok) return;
        store.dispatch('settings/deleteRiskObjective', item).then(() => {
          this.$toast({ component: ToastificationContent, props: { title: 'Updated', text: '✔️ Status changed', icon: 'ThumbsUpIcon', variant: 'success' } });
          this.load();
        });
      });
    },
    onFiltered(items) { this.totalRows = items.length; this.currentPage = 1; },
  },
  created() { this.load(); },
};
</script>
<style lang="scss" scoped>
.per-page-selector { width: 90px; }
</style>
<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
